import { useState, useEffect, useCallback } from "react";
import { useParams, Navigate } from "react-router-dom";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Checkbox } from "@/components/ui/checkbox";
import { useToast } from "@/hooks/use-toast";
import { supabase } from "@/integrations/supabase/client";
import { CheckCircle } from "lucide-react";
import { AnimatedBackground } from "@/components/AnimatedBackground";
import { cn } from "@/lib/utils";
import { PublicFormContent } from "@/components/PublicFormContent";
import { SEO } from "@/components/SEO";

interface FormField {
  id?: string;
  type: string;
  label: string;
  name: string;
  placeholder?: string;
  required?: boolean;
  options?: string[];
  queueField?: string; // Maps to queue item field
}

interface PublicFormData {
  id: string;
  name: string;
  description?: string;
  active: boolean;
  queue_id: string;
  organization_id: string;
  category_id?: string;
  show_organization_picture?: boolean;
  show_animated_background?: boolean;
  background_theme?: string;
  form_fields: FormField[];
  organization: {
    name: string;
    picture_url?: string;
  };
}

export default function PublicForm() {
  const { slug } = useParams<{ slug: string }>();
  const [form, setForm] = useState<PublicFormData | null>(null);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [formData, setFormData] = useState<Record<string, any>>({});
  const { toast } = useToast();

  useEffect(() => {
    if (slug) {
      fetchForm();
    }
  }, [slug]);


  const fetchForm = async () => {
    try {
      const { data, error } = await supabase
        .from('public_forms')
        .select(`
          id,
          name,
          description,
          active,
          queue_id,
          organization_id,
          category_id,
          show_organization_picture,
          show_animated_background,
          background_theme,
          form_fields,
          organization:organizations(name, picture_url)
        `)
        .eq('slug', slug)
        .single();

      if (error) throw error;
      // Fix the organization field structure
      const processedData = {
        ...data,
        organization: Array.isArray(data.organization) ? data.organization[0] : data.organization,
        form_fields: Array.isArray(data.form_fields) ? data.form_fields as any : []
      };
      setForm(processedData);
    } catch (error) {
      console.error('Error fetching form:', error);
      setForm(null);
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = useCallback((fieldName: string, value: any) => {
    setFormData(prev => ({
      ...prev,
      [fieldName]: value
    }));
  }, []);

  const validateForm = () => {
    if (!form) return false;
    
    for (const field of form.form_fields) {
      if (field.required && (!formData[field.name] || formData[field.name].trim() === '')) {
        toast({
          title: "Validation Error",
          description: `${field.label} is required`,
          variant: "destructive"
        });
        return false;
      }
    }
    return true;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!form || !validateForm()) return;

    console.log('Starting form submission...', { form, formData });
    setSubmitting(true);

    try {
      // Map form fields to queue item fields
      const queueItemData: any = {
        // queue_id is resolved in the edge function from the formId to prevent tampering
        status: 'todo',
        priority: 'medium',
        created_by_user_id: null, // External submission
        category_id: form.category_id || null,
      };

      // Group fields by their queue field mapping
      const fieldGroups: Record<string, Array<{ label: string; value: any }>> = {};
      const unmappedFields: Array<{ label: string; value: any }> = [];
      
      for (const field of form.form_fields) {
        const value = formData[field.name];
        if (!value) continue;

        if (field.queueField) {
          // Group fields by queue field
          if (!fieldGroups[field.queueField]) {
            fieldGroups[field.queueField] = [];
          }
          fieldGroups[field.queueField].push({ label: field.label, value });
        } else {
          // Collect unmapped fields
          unmappedFields.push({ label: field.label, value });
        }
      }

      // Process grouped fields
      Object.entries(fieldGroups).forEach(([queueField, fields]) => {
        if (fields.length === 1) {
          // Single field mapping - use the value directly
          queueItemData[queueField] = fields[0].value;
        } else {
          // Multiple fields mapping to same queue field - format with labels
          const formattedData = fields.map(f => `${f.label}: ${f.value}`).join('\n');
          queueItemData[queueField] = formattedData;
        }
      });

      // Set default title if no field is mapped to title
      if (!queueItemData.title) {
        queueItemData.title = `Form Submission - ${form.name}`;
      }

      // Add unmapped fields to description
      if (unmappedFields.length > 0) {
        const existingDescription = queueItemData.description || '';
        const unmappedData = unmappedFields.map(f => `${f.label}: ${f.value}`).join('\n');
        
        queueItemData.description = existingDescription 
          ? `${existingDescription}\n\n${unmappedData}`
          : unmappedData;
      }

      console.log('Prepared queue item data:', queueItemData);

      // Create queue item via secure edge function (bypasses client RLS safely)
      const { data: submitRes, error: submitErr } = await supabase.functions.invoke('submit-public-form', {
        body: {
          formId: form.id,
          queueItemData,
        }
      });

      if (submitErr || !submitRes?.success) {
        throw submitErr || new Error(submitRes?.error || 'Failed to submit form');
      }

      const newQueueItemId = submitRes.queueItemId as string;

      // Only trigger webhooks if organization has active webhooks
      try {
        const { data: webhooks } = await supabase
          .from('webhooks')
          .select('id')
          .eq('organization_id', form.organization_id)
          .eq('is_active', true)
          .or(`queue_id.is.null,queue_id.eq.${form.queue_id}`)
          .limit(1);

        if (webhooks && webhooks.length > 0) {
          await supabase.functions.invoke('send-webhook', {
            body: {
              organizationId: form.organization_id,
              queueId: form.queue_id,
              queueItemId: newQueueItemId,
              formData: formData,
              queueItemData: { id: newQueueItemId, ...queueItemData }
            }
          });
        }
      } catch (webhookError) {
        console.error('Webhook error (non-blocking):', webhookError);
      }

      setSubmitted(true);
      toast({
        title: "Success",
        description: "Your submission has been received successfully!"
      });
    } catch (error) {
      console.error('Form submission error:', error);
      toast({
        title: "Error",
        description: "Failed to submit form. Please try again.",
        variant: "destructive"
      });
    } finally {
      setSubmitting(false);
    }
  };

  const renderField = useCallback((field: FormField) => {
    const fieldId = `field-${field.name}`;
    
    switch (field.type) {
      case 'text':
      case 'email':
      case 'tel':
      case 'number':
        return (
          <div key={field.name} className="space-y-2">
            <Label htmlFor={fieldId}>
              {field.label}
              {field.required && <span className="text-destructive ml-1">*</span>}
            </Label>
            <Input
              id={fieldId}
              type={field.type}
              placeholder={field.placeholder}
              value={formData[field.name] || ''}
              onChange={(e) => handleInputChange(field.name, e.target.value)}
              required={field.required}
            />
          </div>
        );
      
      case 'textarea':
        return (
          <div key={field.name} className="space-y-2">
            <Label htmlFor={fieldId}>
              {field.label}
              {field.required && <span className="text-destructive ml-1">*</span>}
            </Label>
            <Textarea
              id={fieldId}
              placeholder={field.placeholder}
              value={formData[field.name] || ''}
              onChange={(e) => handleInputChange(field.name, e.target.value)}
              required={field.required}
            />
          </div>
        );
      
      case 'select':
        return (
          <div key={field.name} className="space-y-2">
            <Label htmlFor={fieldId}>
              {field.label}
              {field.required && <span className="text-destructive ml-1">*</span>}
            </Label>
            <Select 
              value={formData[field.name] ?? undefined}
              onValueChange={(value) => handleInputChange(field.name, value)}
            >
              <SelectTrigger>
                <SelectValue placeholder={field.placeholder || "Select an option"} />
              </SelectTrigger>
              <SelectContent className="z-50 bg-background">
                {field.options?.filter(Boolean).map((option, index) => (
                  <SelectItem key={index} value={option}>
                    {option}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        );
      
      case 'checkbox':
        return (
          <div key={field.name} className="space-y-3">
            <Label className="text-sm font-medium">
              {field.label}
              {field.required && <span className="text-destructive ml-1">*</span>}
            </Label>
            <div className="space-y-2">
              {field.options && field.options.length > 0 ? (
                field.options.filter(Boolean).map((option, optIndex) => {
                  const optionId = `${fieldId}_${optIndex}`;
                  const currentValues = Array.isArray(formData[field.name]) ? formData[field.name] : [];
                  
                  return (
                    <div key={optIndex} className="flex items-center space-x-2">
                      <Checkbox
                        id={optionId}
                        checked={currentValues.includes(option)}
                        onCheckedChange={(checked) => {
                          const newValues = checked 
                            ? [...currentValues, option]
                            : currentValues.filter((v: string) => v !== option);
                          handleInputChange(field.name, newValues);
                        }}
                      />
                      <Label htmlFor={optionId} className="text-sm cursor-pointer">
                        {option}
                      </Label>
                    </div>
                  );
                })
              ) : (
                <div className="flex items-center space-x-2">
                  <Checkbox
                    id={fieldId}
                    checked={formData[field.name] || false}
                    onCheckedChange={(checked) => handleInputChange(field.name, checked)}
                    required={field.required}
                  />
                  <Label htmlFor={fieldId} className="text-sm cursor-pointer">
                    {field.placeholder || field.label}
                  </Label>
                </div>
              )}
            </div>
          </div>
        );
      
      default:
        return null;
    }
  }, [formData, handleInputChange]);

  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <Card className="w-full max-w-2xl mx-4">
          <CardContent className="p-8">
            <div className="animate-pulse space-y-4">
              <div className="h-6 bg-muted rounded w-1/2"></div>
              <div className="h-4 bg-muted rounded w-3/4"></div>
              <div className="space-y-3">
                <div className="h-4 bg-muted rounded"></div>
                <div className="h-10 bg-muted rounded"></div>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (!form) {
    return <Navigate to="/404" replace />;
  }

  if (!form.active) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <Card className="w-full max-w-2xl mx-4">
          <CardContent className="p-8 text-center">
            <div className="mb-6">
              <h1 className="text-2xl font-bold text-foreground mb-2">
                Form Currently Unavailable
              </h1>
              <p className="text-muted-foreground mb-4">
                This form is currently inactive and not accepting submissions.
              </p>
              <p className="text-sm text-muted-foreground">
                Please contact {form.organization.name} for more information.
              </p>
            </div>
            <Button 
              onClick={() => window.location.href = '/'}
              variant="default"
            >
              Back Home
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (submitted) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <Card className="w-full max-w-2xl mx-4">
          <CardContent className="p-8 text-center">
            <div className="mb-6">
              <CheckCircle className="w-16 h-16 text-green-500 mx-auto mb-4" />
              <h1 className="text-2xl font-bold text-foreground mb-2">
                Thank You!
              </h1>
              <p className="text-muted-foreground">
                Your submission has been received successfully. We'll get back to you soon.
              </p>
            </div>
            <Button 
              onClick={() => {
                setSubmitted(false);
                setFormData({});
              }}
              variant="outline"
            >
              Submit Another Response
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  const getThemeClasses = (theme: string) => {
    const themes = {
      purple: "animated-gradient-bg-purple",
      blue: "animated-gradient-bg-blue", 
      yellow: "animated-gradient-bg-yellow",
      green: "animated-gradient-bg-green",
      red: "animated-gradient-bg-red"
    };
    return themes[theme as keyof typeof themes] || "animated-gradient-bg-purple";
  };

  const getButtonClasses = (theme: string) => {
    const buttonThemes = {
      purple: "bg-purple-600 hover:bg-purple-700 focus:ring-purple-500",
      blue: "bg-blue-600 hover:bg-blue-700 focus:ring-blue-500",
      yellow: "bg-yellow-500 hover:bg-yellow-600 focus:ring-yellow-400",
      green: "bg-green-600 hover:bg-green-700 focus:ring-green-500",
      red: "bg-red-600 hover:bg-red-700 focus:ring-red-500"
    };
    return buttonThemes[theme as keyof typeof buttonThemes] || "bg-purple-600 hover:bg-purple-700 focus:ring-purple-500";
  };


  // Form content extracted to PublicFormContent component to prevent remounts

  const structuredData = {
    "@context": "https://schema.org",
    "@type": "WebPage",
    "name": form.name,
    "description": form.description || `Submit a request to ${form.organization.name}`,
    "provider": {
      "@type": "Organization",
      "name": form.organization.name
    },
    "mainEntity": {
      "@type": "ContactPage",
      "name": form.name,
      "description": form.description
    }
  };

  if (form?.show_animated_background) {
    return (
      <>
        <SEO 
          title={`${form.name} | ${form.organization.name}`}
          description={form.description || `Submit a request to ${form.organization.name} using this form.`}
          url={`https://queueos.com/forms/${slug}`}
          structuredData={structuredData}
        />
        <AnimatedBackground className={cn("min-h-screen w-full fixed inset-0", getThemeClasses(form.background_theme || "purple"))}>
          <div className="min-h-screen w-full flex items-center justify-center py-8 px-4">
            <PublicFormContent
              form={form}
              formData={formData}
              submitting={submitting}
              onChange={handleInputChange}
              onSubmit={handleSubmit}
            />
          </div>
        </AnimatedBackground>
      </>
    );
  }

  return (
    <>
      <SEO 
        title={`${form.name} | ${form.organization.name}`}
        description={form.description || `Submit a request to ${form.organization.name} using this form.`}
        url={`https://queueos.com/forms/${slug}`}
        structuredData={structuredData}
      />
      <div className="min-h-screen bg-background flex items-center justify-center py-8">
        <PublicFormContent
          form={form}
          formData={formData}
          submitting={submitting}
          onChange={handleInputChange}
          onSubmit={handleSubmit}
        />
      </div>
    </>
  );
}